import { AccommodationListing } from "@/types/accommodation";
import { useQuery } from "@tanstack/react-query";
import api from "@/config/axiosConfig";

export const ITEMS_PER_PAGE = 20;

interface NearbyHomeStaysResponse {
  data: AccommodationListing[];
  nextCursor: number | null;
  total: number;
}

export const nearbySearchPlum = async (
  accommodationBounds: {
    _sw: { lng: number; lat: number };
    _ne: { lng: number; lat: number };
  } | null,
  offset: number = 0,
  selectedCurrency: string,
  selectedPriceRange: string | null,
  numNights: number = 1
): Promise<NearbyHomeStaysResponse> => {
  try {
    const response = await api.lodgings.get<NearbyHomeStaysResponse>(
      "/home-stays",
      {
        params: {
          sw_lng: accommodationBounds?._sw.lng,
          sw_lat: accommodationBounds?._sw.lat,
          ne_lng: accommodationBounds?._ne.lng,
          ne_lat: accommodationBounds?._ne.lat,
          offset: offset,
          limit: ITEMS_PER_PAGE,
          to_currency: selectedCurrency,
          price_range: selectedPriceRange,
          num_nights: numNights,
        },
      }
    );

    const nextCursor =
      response.data.data.length === ITEMS_PER_PAGE
        ? offset + ITEMS_PER_PAGE
        : null;

    return {
      ...response.data,
      nextCursor,
    };
  } catch (error) {
    console.error("Error fetching nearby hotels:", error);
    throw new Error("Failed to fetch nearby hotels");
  }
};

export const useNearbyHomeStays = (
  accommodationBounds: {
    _sw: { lng: number; lat: number };
    _ne: { lng: number; lat: number };
  } | null,
  offset: number,
  selectedCurrency: string,
  selectedPriceRange: string | null,
  numNights: number = 1
) => {
  return useQuery({
    queryKey: [
      "nearbyHomeStays",
      accommodationBounds,
      offset,
      selectedCurrency,
      selectedPriceRange,
    ],
    queryFn: () =>
      nearbySearchPlum(
        accommodationBounds,
        offset,
        selectedCurrency,
        selectedPriceRange,
        numNights
      ),
    enabled: !!accommodationBounds,
    staleTime: Infinity,
  });
};

export const checkHomeStayAvailability = async (
  listingId: string,
  checkIn: string,
  checkOut: string
): Promise<{ isAvailable: boolean; reason?: string }> => {
  const formattedCheckIn = new Date(checkIn).toISOString().split("T")[0];
  const formattedCheckOut = new Date(checkOut).toISOString().split("T")[0];

  const response = await api.lodgings.get(
    `/home-stays/${listingId}/availability`,
    {
      params: { check_in: formattedCheckIn, check_out: formattedCheckOut },
    }
  );

  const data = response.data;

  return {
    isAvailable: data.isAvailable,
    ...(data.reason && { reason: data.reason }),
  };
};
